import { useEffect, useState, useRef } from 'react'

export default function ProgressIndicator({
  value = 33,
  size = 300,
  strokeWidth = 20,
  duration = 1600,
  label = 'PROGRESS INDICATOR CIRCLE',
}) {
  const [progress, setProgress] = useState(0)
  const [displayValue, setDisplayValue] = useState(0)
  const rafRef = useRef()
  const startRef = useRef()
  const prevValue = useRef(0)

  // Derived geometry
  const half = size / 2
  const radius = half - strokeWidth / 2 - 2 // leave a little room
  const circumference = 2 * Math.PI * radius

  // Animate progress & numeric counter using requestAnimationFrame
  useEffect(() => {
    const start = performance.now()
    startRef.current = start
    const from = prevValue.current
    const to = Math.max(0, Math.min(100, value))
    prevValue.current = to

    function tick(now) {
      const elapsed = now - start
      const t = Math.min(1, elapsed / duration)
      // easeOutCubic for a smoother feel
      const ease = 1 - Math.pow(1 - t, 3)
      const current = from + (to - from) * ease
      setProgress(current)
      setDisplayValue(Math.round(current))

      if (t < 1) rafRef.current = requestAnimationFrame(tick)
    }

    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [value, duration])

  // SVG stroke-dashoffset calculation (we want stroke to *grow* clockwise from bottom)
  // default circle stroke starts at 3 o'clock. We rotate the circle by +90deg so it starts at 6 o'clock (bottom).
  const dashArray = circumference
  const dashOffset = circumference * (1 - progress / 100)

  // Gradient and glow colors — tweak to taste
  const gradientId = 'grad-arc'

  return (
    <div style={{ width: '100%', height: size, display: 'inline-block' }}>
      <svg
        width={size}
        height={size}
        viewBox={`0 ${-10} ${size} ${size + 20}`}
        style={{ width: '100%' }}
      >
        <defs>
          <linearGradient id={gradientId} x1='0%' x2='100%' y1='0%' y2='0%'>
            <stop offset='0%' stopColor='#d6ccff' />
            <stop offset='55%' stopColor='#8b5cf6' />
            <stop offset='100%' stopColor='#6366f1' />
          </linearGradient>

          {/* A subtle gaussian blur filter for glow when used via filter:url(#glow) */}
          <filter id='svg-glow' x='-40%' y='-40%' width='180%' height='180%'>
            <feGaussianBlur stdDeviation='6' result='coloredBlur' />
            <feMerge>
              <feMergeNode in='coloredBlur' />
              <feMergeNode in='SourceGraphic' />
            </feMerge>
          </filter>
        </defs>

        {/* Outer dotted ring */}
        <circle
          cx={half}
          cy={half}
          r={radius + strokeWidth * 0.9}
          fill='none'
          stroke='#2b2b3a'
          strokeWidth={2}
          strokeDasharray='2 18' // small dash (dot) then big gap
          strokeLinecap='round'
          opacity={0.9}
        />

        {/* Background track */}
        <circle
          cx={half}
          cy={half}
          r={radius}
          fill='none'
          stroke='#252532'
          strokeWidth={strokeWidth}
          strokeLinecap='round'
          transform={`rotate(90 ${half} ${half})`} // start at bottom
          style={{ mixBlendMode: 'normal' }}
        />

        {/* Completed arc (foreground) */}
        <g>
          <circle
            cx={half}
            cy={half}
            r={radius}
            fill='none'
            stroke={`url(#${gradientId})`}
            strokeWidth={strokeWidth}
            strokeLinecap='round'
            transform={`rotate(90 ${half} ${half})`}
            strokeDasharray={dashArray}
            strokeDashoffset={dashOffset}
            style={{
              // removed CSS transition so the JS-driven `progress` controls the motion
              filter:
                'drop-shadow(0 0 6px rgba(124,58,237,0.55)) drop-shadow(0 0 18px rgba(99,102,241,0.2))',
            }}
            className='completed-arc'
          />

          {/* softer blurred glow layer */}
          <circle
            cx={half}
            cy={half}
            r={radius}
            fill='none'
            stroke={`url(#${gradientId})`}
            strokeWidth={strokeWidth + 6}
            strokeLinecap='round'
            transform={`rotate(90 ${half} ${half})`}
            strokeDasharray={dashArray}
            strokeDashoffset={dashOffset}
            style={{
              opacity: 0.28,
              filter: 'blur(6px)',
            }}
          />
        </g>

        {/* A small handle/dot at the start of the arc (bottom) — purely decorative */}
        <g>
          {/* compute handle position for the current progress end so the glow head looks natural */}
          {(() => {
            // angle in radians: start at bottom (90deg), then progress goes clockwise
            const angle = Math.PI / 2 + 2 * Math.PI * (progress / 100)
            // end coordinates
            const hx = half + radius * Math.cos(angle)
            const hy = half + radius * Math.sin(angle)
            return (
              <circle
                cx={hx}
                cy={hy}
                r={strokeWidth / 2}
                fill='#eef2ff'
                style={{
                  opacity: 1,
                  filter: 'drop-shadow(0 0 6px rgba(124,58,237,0.6))',
                }}
              />
            )
          })()}
        </g>

        {/* Center text (number and label) */}
        <g transform={`translate(${half}, ${half - 6})`} textAnchor='middle' className='no-select'>
          <text
            y={0}
            fill='#ececff'
            fontFamily="Inter, Roboto, system-ui, -apple-system, 'Segoe UI', sans-serif"
            fontSize={Math.max(32, size * 0.12)}
            fontWeight={700}
          >
            {displayValue} %
          </text>

          <text
            y={Math.max(26, size * 0.09)}
            fill='#ececff'
            fontFamily="Inter, Roboto, system-ui, -apple-system, 'Segoe UI', sans-serif"
            fontSize={Math.max(10, size * 0.05)}
            letterSpacing={1.5}
            opacity={0.7}
          >
            {label}
          </text>
        </g>

        {/* Add a small dotted ring outside for extra style (mirrors the outer dotted) */}
        <circle
          cx={half}
          cy={half}
          r={radius + strokeWidth * 1.35}
          fill='none'
          stroke='#15151b'
          strokeWidth={2}
          strokeDasharray='1 14'
          strokeLinecap='round'
          opacity={0.5}
        />
      </svg>

      {/* CSS for pulsing glow applied to the completed arc (only) */}
      <style>{`
        /* animate the drop-shadow filter to pulse the glow only on the completed segment */
        @keyframes glowPulse {
          0% {
            filter: drop-shadow(0 0 4px rgba(124,58,237,0.35)) drop-shadow(0 0 10px rgba(99,102,241,0.15));
          }
          50% {
            filter: drop-shadow(0 0 12px rgba(124,58,237,0.85)) drop-shadow(0 0 28px rgba(99,102,241,0.35));
          }
          100% {
            filter: drop-shadow(0 0 4px rgba(124,58,237,0.35)) drop-shadow(0 0 10px rgba(99,102,241,0.15));
          }
        }

        /* Use the animation on the visible arc (first .completed-arc circle). We target it inside the component scope. */
        .completed-arc {
          animation: glowPulse 2.2s ease-in-out infinite;
        }

        /* Prevent accidental text selection when clicking */
        .no-select { user-select: none; }
      `}</style>
    </div>
  )
}

/*
Usage example:

import ProgressIndicator from './ProgressIndicator'

function App(){
  return <ProgressIndicator value={33} size={360} strokeWidth={24} duration={1200} />
}
*/
